import React, { Suspense } from 'react';

import { useLocation } from 'react-router-dom';
import { injectStores } from '@stores/factories/store-utils';
import { HeaderProps } from './utils/header-interfaces';
import useJakhubMenuHeight from '@/custom-hooks/useJakhubMenuHeight';

import ContactForms from '../../common/contact-forms/contact-forms';
import DivButton from '../../common/legos/div-button/div-button';
import EmployeeAdminMenu from '../menu/employee-admin-menu/employee-admin-menu';
import HeaderHelmet, { locationToModuleId, countSubModules } from './header-helmet';
import HeaderTools from './utils/header-tools';
import LocalhostDetector from '@components/modules/general/localhost-detector/localhost-detector';
import MainMenu from '../menu/main-menu/main-menu';
import MobileMenu from '../menu/mobile-menu/mobile-menu';
import PortalDrawer from '../../common/portal-drawer/portal-drawer';
import SpecialMenuMessage from '../../common/menu-message/special-menu-message';
import SubMenu from '../menu/main-menu/sub-menu';
import UnverifiedUserMenuMessage from '../../common/menu-message/unverified-user-menu-message';

const SettingsTopMenu = React.lazy(() =>
    import('../../modules/account-settings/account-settings').then((mod) => ({
        default: mod.SettingsTopMenu,
    }))
);

import './header.scss';
import ModuleNavigation from '@components/modules/module-navigation';
import { ModuleRoute } from '@modules/utils/module-interfaces';

function Header(props: HeaderProps) {
    const {
        ApplicationStore: {
            headerTools: { mobileMenuOpened },
            toggleMobileMenu,
        },
        minimal = false,
    } = props;
    const { pathname } = useLocation();
    useJakhubMenuHeight();

    const menuState = locationToModuleId(pathname);
    const showSubMenu = countSubModules(menuState.currentModuleId) > 0;

    if (minimal) {
        return (
            <header id='jakhub-menu-header' className='jakhub-menu-header'>
                <div className='header-main' style={{ placeItems: 'center' }}>
                    <div className='header-main-content'>
                        <div id='main-menu' className='main-menu'>
                            <ModuleNavigation
                                className='menu-home'
                                selectedModules={['HOME']}
                                predicate={(module: ModuleRoute) => module.id === 'HOME'}
                                menuState={menuState}
                            />
                        </div>
                    </div>
                </div>
            </header>
        );
    }

    return (
        <header id='jakhub-menu-header' className='jakhub-menu-header'>
            <HeaderHelmet />
            <LocalhostDetector />
            <EmployeeAdminMenu />
            <div className='header-main'>
                <nav className='header-main-content'>
                    <DivButton className='hamburger' onClick={toggleMobileMenu}>
                        <i className='material-icons'>{!mobileMenuOpened ? 'menu' : 'close'}</i>
                    </DivButton>
                    <MainMenu menuState={menuState} />
                    <HeaderTools />
                </nav>
            </div>
            {showSubMenu ? <SubMenu menuState={menuState} /> : null}
            {menuState.currentModuleId === 'SETTINGS' ? (
                <Suspense fallback={<div />}>
                    <SettingsTopMenu />
                </Suspense>
            ) : null}
            <SpecialMenuMessage />
            <UnverifiedUserMenuMessage />
            <MobileMenu menuState={menuState} />
            <PortalDrawer />
            <ContactForms />
        </header>
    );
}

export default injectStores(['ApplicationStore'])(Header);
